import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { MissingStoreAttachmentTokenError } from '@errors/missing-store-attachment-token-error';
import { GraphQLError } from 'graphql/error';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  /**
   * Default error message to display when an error occurs
   */
  public get defaultErrorMessage(): string {
    return `We are experiencing technical difficulties. Please contact customer service at ${environment.email} for assistance.`;
  }

  /**
   * Handle for laravel api response, returns an array of errors
   *
   * @param {HttpErrorResponse} response Http error response that contains the error code and error messages
   */
  public handleResponseError(response: HttpErrorResponse): string[] {
    switch (true) {
      case response.status === 422:
        return Object.keys(response.error)
          .map((errKey) => response.error[errKey])
          .flat();

      case (response.status === 400 || response.status === 403) && response.error?.message?.length > 0:
        return [response.error.message];

      case response.status >= 500 && response.status <= 599:
        return [
          `${this.defaultErrorMessage} <br/> Error Code: ${response.status} Error Message: ${response.error?.message}`,
        ];

      default:
        return [this.defaultErrorMessage];
    }
  }

  /**
   * Handle errors for the consultation submission requests chain
   *
   * @param {MissingStoreAttachmentTokenError | HttpErrorResponse} response the error object
   */
  public handleConsultationSubmissionError(error: MissingStoreAttachmentTokenError | HttpErrorResponse): string[] {
    return error instanceof MissingStoreAttachmentTokenError
      ? [error.message]
      : this.handleResponseError(error as HttpErrorResponse);
  }

  /**
   * Parse GraphQL response errors into a set of unique readable error messages
   */
  public parseGraphQLResponseErrors(errors: GraphQLError[]): string[] {
    return Array.from(new Set(errors.map((error) => error.message)));
  }

  /**
   * Checks if an error is a Credit Card Decline error.
   *
   * @param {any} error the error object to check
   *
   * @returns {boolean} true if the error is a Credit Card Decline error
   */
  public isCreditCardDeclineError(error: any): boolean {
    return error instanceof HttpErrorResponse && error.error?.message?.includes('Credit Card Decline');
  }
}
